import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { plansData } from '../utils/dummydata';
import BusinessLocationForm from '../components/onboarding/BusinessLocationForm';
import ShopPhotoUpload from '../components/onboarding/ShopPhotoUpload';
import WhatDoYouOffer from '../components/onboarding/WhatDoYouOffer';
import WorkingHours from '../components/onboarding/WorkingHours';
import ReviewDetailsStep from '../components/onboarding/ReviewDetailsStep';
import { storage } from '../firebase/firebaseConfig';
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { functionsBaseUrl } from "../config/runtime";
import { authenticatedJson, ensureGuestSession } from "../utils/guestSession";
import { auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import '../v2/workflows.css';

const onboardingStages = [
    { title: "You", description: "You will sign in with this, and it is where we email you when your listing is live." },
    { title: "Your business", description: "Help customers recognize your business and find you." },
    { title: "What you do", description: "Choose your services and usual opening hours." },
    { title: "Review", description: "Check your listing details. We charge the first month only after we approve your listing." },
];

const OnBoarding = () => {
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState(() => { const type = new URLSearchParams(window.location.search).get('type'); return plansData[{ondemand:'printShop',specialist:'specialist',supplier:'supplier'}[type] || 'printShop']; });
    const [showPassword, setShowPassword] = useState(false);
    const [creatingAccount, setCreatingAccount] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        shopName: "",
        ownerName: "",
        shopEmail: "",
        phone: "",
        reviews: "",
        description: "",
        streetAddress: "",
        city: "",
        province: "",
        postalCode: "",
        shopPhotoPreview: "",
        shopPhotoFile: null,
        shopPhotoFileName: "",
        selectedSupplies: [],
        inkTypes: [],
        customSupplies: [],
        workingHours: {
            Monday: { open: true, from: "09:00", to: "18:00" },
            Tuesday: { open: true, from: "09:00", to: "18:00" },
            Wednesday: { open: true, from: "09:00", to: "18:00" },
            Thursday: { open: true, from: "09:00", to: "18:00" },
            Friday: { open: true, from: "09:00", to: "18:00" },
            Saturday: { open: true, from: "09:00", to: "18:00" },
            Sunday: { open: false, from: "09:00", to: "18:00" },
        },
        password: "",
        confirmPassword: "",

    });
    useEffect(() => () => { if (form.shopPhotoPreview?.startsWith('blob:')) URL.revokeObjectURL(form.shopPhotoPreview); }, [form.shopPhotoPreview]);
    const [step, setStep] = useState(1);
    const currentStepData = onboardingStages[step - 1];

    const emailExists = false;


    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const canContinue = () => {
        switch (step) {
            case 1:
                return form.ownerName.trim() && isValidEmail(form.shopEmail) && form.phone.replace(/\D/g, '').length >= 9 && form.password.length >= 8;

            case 2:
                return (
                    selectedPlan && form.shopName?.trim() &&
                    form.ownerName?.trim() &&
                    form.shopEmail?.trim() &&
                    isValidEmail(form.shopEmail) &&
                    form.streetAddress?.trim() &&
                    form.city?.trim()
                );

            case 3:
                return (
                    (form.selectedSupplies?.length > 0) ||
                    (form.customSupplies?.length > 0)
                );

            case 4:
                return (
                    form.shopEmail?.trim() &&
                    isValidEmail(form.shopEmail) &&
                    form.password?.length >= 8 &&
                    form.password === form.confirmPassword &&
                    !emailExists
                );

            default:
                return false;
        }
    };



    const handleCompleteSetup = async () => {
        try {
            setCreatingAccount(true);

            await ensureGuestSession();

            let shopPhotoURL = "";
            let tempFileName = "";

            if (form.shopPhotoFile) {
                const file = form.shopPhotoFile;
                const guest = await ensureGuestSession();

                const extension = file.name.split(".").pop();

                tempFileName =
                    `onboarding/${guest.uid}/${Date.now()}-${Math.random()
                        .toString(36)
                        .substring(2)}.${extension}`;

                const imageRef = storageRef(storage, tempFileName);

                await uploadBytes(imageRef, file);

                shopPhotoURL = await getDownloadURL(imageRef);
            }

            const pendingData = {
                selectedPlan,
                form: {
                    ...form,
                    shopPhotoFile: null,
                    shopPhotoPreview: "",
                    shopPhotoURL,
                    shopPhotoFileName: tempFileName,
                },
            };

            const completion = await authenticatedJson(`${functionsBaseUrl}/completeOnboarding`, {
                selectedPlan: pendingData.selectedPlan,
                form: pendingData.form,
            });

            await signOut(auth);
            navigate(`/shop-login?success=true${completion.reviewEmailSent ? "" : "&reviewEmail=failed"}`, {
                replace: true,
            });

        } catch (error) {
            console.error(error);
            setError(error.message);
            setCreatingAccount(false);
        }
    };



    return (
        <div className="v2-onboarding-shell">
            <Helmet>
                <title>Create account - Signet Print</title>
                <meta
                    name="description"
                    content="Create a new account on Signet Print."
                />
            </Helmet>
            <header className="v2-flow-header">
                <Link className="v2-flow-brand" to="/" aria-label="SignetPrint home"><span className="v2-flow-mark" aria-hidden="true"><svg viewBox="0 0 52 52" fill="currentColor"><path d="M26 52C40.36 52 52 40.36 52 26S40.36 0 26 0v52Z"/><path d="M0 52c14.36 0 26-11.64 26-26S14.36 0 0 0v52Z"/></svg></span><span>Signet<span>Print</span></span></Link>
                <nav aria-label="Main navigation"><Link to="/marketplace">Marketplace</Link><Link to="/contact">Contact</Link><Link to="/file-check">File Check</Link></nav>
                <Link className="v2-flow-login" to="/shop-login">Store login</Link>
            </header>
            <main className="v2-onboarding-main">
                <section className="v2-onboarding-content">
                    <div className="v2-onboarding-title"><h1>List your business</h1><p>About five minutes. We check every listing before it goes live, and nothing is charged until it does.</p></div>
                    <div className="v2-onboarding-steps" aria-label={`Step ${step} of 4`}>
                        {onboardingStages.map((stage, index) => <div className={`v2-onboarding-step ${index + 1 === step ? "is-current" : index + 1 < step ? "is-done" : ""}`} key={stage.title}><span>{index + 1 < step ? "✓" : index + 1}</span><b>{stage.title}</b></div>)}
                    </div>

                    <div className="v21-signup-layout"><div>
                    <div className="v2-onboarding-form">
                        {step === 1 && <section className="v21-signup-card"><h2>About you</h2><p>{currentStepData.description}</p><div className="v21-signup-fields"><label className="v2-field full"><span>Your name</span><input name="ownerName" autoComplete="name" value={form.ownerName} onChange={e => setForm({...form,ownerName:e.target.value})} placeholder="Your full name" /></label><label className="v2-field"><span>Email</span><input name="shopEmail" autoComplete="email" type="email" value={form.shopEmail} onChange={e=>setForm({...form,shopEmail:e.target.value})} placeholder="you@yourshop.co.za" /></label><label className="v2-field"><span>Cell number</span><input name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="082 123 4567" /></label><label className="v2-field full"><span>Password</span><div className="v21-password"><input autoComplete="new-password" type={showPassword?'text':'password'} value={form.password} onChange={e=>setForm({...form,password:e.target.value,confirmPassword:e.target.value})} placeholder="At least 8 characters" /><button type="button" aria-pressed={showPassword} onClick={()=>setShowPassword(!showPassword)}>{showPassword?'Hide':'Show'}</button></div><small>Your counter staff can use the same login, so pick one you are happy to share with them.</small></label></div></section>}
                        {step === 2 && <><section className="v21-signup-card"><h2>Your business</h2><div className="v21-signup-fields"><label className="v2-field full"><span>Business type</span><select value={selectedPlan?.key || 'printShop'} onChange={e=>setSelectedPlan(plansData[e.target.value])}><option value="printShop">On-Demand Print — R349 a month</option><option value="specialist">Specialist Print — R199 a month</option><option value="supplier">Print Supplier — R199 a month</option></select></label><label className="v2-field full"><span>Business name</span><input name="shopName" value={form.shopName} onChange={e=>setForm({...form,shopName:e.target.value})} /></label><label className="v2-field full"><span>One line about what you do</span><textarea name="description" maxLength="1000" rows="3" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} /></label></div></section><BusinessLocationForm form={form} setForm={setForm} /><ShopPhotoUpload form={form} setForm={setForm} /></>}

                        {step === 3 && (
                            <>
                                <WhatDoYouOffer form={form} setForm={setForm} selectedPlan={selectedPlan} />
                                <WorkingHours form={form} setForm={setForm} />
                            </>
                        )}

                        {step === 4 && (
                            <ReviewDetailsStep
                                form={form}
                                selectedPlan={selectedPlan}
                                setStep={(oldStep) => setStep(oldStep <= 1 ? 1 : oldStep <= 4 ? 2 : 3)}
                            />
                        )}
                        {error && <p className="form-error" role="alert">{error}</p>}
                        {step === 4 && <div className="v21-signup-card"><h2>Payment after approval</h2><p>Submit your listing for review. If approved, we email a secure Paystack checkout link for the first month. Your listing stays private until payment and subscription confirmation.</p></div>}



                    </div>
            <div className="v2-onboarding-footer">
                <div className="v2-onboarding-footer-inner">
                    <div className="flex items-center gap-[12px] min-w-0">
                        {step === 1 && <span className="v21-signup-login">Already listed? <Link to="/shop-login">Store login</Link></span>}
                        {step > 1 && (
                            <button
                                onClick={() => setStep(step - 1)}
                                className="v2-flow-back"
                            >
                                Back
                            </button>
                        )}
                    </div>
                    <button
                        disabled={!canContinue() || creatingAccount}
                        onClick={() => {
                            if (step === 4) {
                                handleCompleteSetup();
                            } else {
                                setStep(step + 1);
                            }
                        }}
                        className="v2-button v2-button-fill v2-flow-continue"
                    >
                        {step === 4
                            ? (creatingAccount ? "Creating account..." : "Complete Setup")
                            : "Continue"
                        }
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-right w-[16px] h-[16px]">
                            <path d="M5 12h14">
                            </path>
                            <path d="m12 5 7 7-7 7"></path>
                        </svg>
                    </button>
                </div>
            </div>
                    </div><aside className="v21-signup-preview" aria-label="Your card preview"><p><b>Your card</b> · How customers will see you</p><article><div className="v21-preview-image">{form.shopPhotoPreview ? <img src={form.shopPhotoPreview} alt="Your listing preview" /> : <span>{(form.shopName || 'Your shop').split(' ').slice(0,2).map(word=>word[0]).join('').toUpperCase()}</span>}</div><div><h2>{form.shopName || 'Your business name'}</h2><p>{form.city || 'Your suburb'}</p><p>{form.description || 'One line about what you do.'}</p><p>{form.selectedSupplies.join(' · ') || 'Your services'}</p><span className="v21-preview-action">{selectedPlan?.key==='printShop'?'Send files':'Enquire'}</span></div></article><div className="v21-preview-price">Print shops customers send files to are <b>R349 a month</b>. Specialists and suppliers are <b>R199</b>. VAT included, nothing charged until you are approved. Renews automatically each month. Cancel renewal in account settings and keep access until your paid period ends. Refund requests are reviewed manually.</div></aside></div>
                </section>
            </main>


        </div>
    )
}

export default OnBoarding;
